import * as React from 'react';
import { Box } from '@chakra-ui/react';
import {
  HStack,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Button } from 'components/ui/button';
import { CareTeamSection } from './CareTeamSection';
import { MemberStatusSection } from './MemberStatusSection';
import { AppointmentsSection } from './AppointmentsSection';
import { CareCoordinationSection } from './CareCoordinationSection';
import { EnrollmentStatusSection } from './EnrollmentStatusSection';
import { MemberAttributesSection } from './MemberAttributesSection';
import { RosterFilters } from './types';

// Wider than the 264 the controls need: the panel now carries its own
// padding inside a filled background, and the controls should keep the width
// they had rather than absorbing it.
const SIDEBAR_WIDTH = 296;

/**
 * The roster's filter column.
 *
 * Replaces the horizontal toolbar that used to sit above the list. That
 * toolbar wrapped onto five rows at typical widths and left roughly two
 * patients visible per scroll; moving the same controls into a fixed-width
 * column hands the vertical axis back to the list, which is the screen's
 * actual subject.
 *
 * A flex column, not one scrolling box: the filter groups sit in their own
 * scrollable region up top, and the Apply Filters footer is a non-scrolling
 * sibling below it — a plain flexbox split does the "stays at the bottom of
 * the window as you scroll the panel" job on its own, without `position:
 * sticky` fighting the scroll container.
 *
 * The feather fill is what makes this read as a panel rather than as controls
 * floating next to the list — with the page header gone there is no other
 * boundary telling you where the filters stop and the patients start. It runs
 * flush to the side nav and the workspace toolbar and takes the full height,
 * so it reads as a wall of the layout rather than a floating card.
 */
export const FilterSidebar = ({ filters }: { filters: RosterFilters }) => (
  <Box
    as="aside"
    w={`${SIDEBAR_WIDTH}px`}
    flexShrink={0}
    h="100%"
    display="flex"
    flexDirection="column"
    bg="feather"
  >
    <Box
      flex={1}
      minH={0}
      overflowY="auto"
      px={4}
      pt={4}
      // A little clearance for the last control's dropdown menu, not the
      // deep buffer this used to need — `menuPlacement="auto"` on
      // `FilterSelect` is what actually keeps a menu from running off the
      // bottom of the window, and the Apply footer below already occupies
      // the space that padding used to reserve.
      pb={4}
    >
      <HStack justifyContent="space-between" alignItems="center" mb={4}>
        <Text my={0} fontSize="sm" fontWeight="semibold" color="secondary.500">
          Filters
        </Text>

        {filters.activeFilterCount > 0 && (
          <Button
            variant="link"
            size="xs"
            onClick={filters.clearAllFilters}
            aria-label={`Clear all ${filters.activeFilterCount} filters`}
          >
            Clear All
          </Button>
        )}
      </HStack>

      <VStack align="stretch" gap={0}>
        {/* Appointments sits directly under care team: both answer "who am I
            seeing, and when", which is the question the roster gets opened for.
            The slower-moving clinical attributes follow.

            Enrollment Status follows Care Coordination because the two are
            read together — the funnel stage, then whether the member is newly
            arrived or has left. They were one group until round 6 split them
            (see `EnrollmentStatusSection`), so keeping them adjacent preserves
            the reading order people already learned. Member Status moved below
            both (Aug 2026 filter-logic pass) rather than between them, for the
            same reason — it would have split that pair. */}
        <CareTeamSection filters={filters} />
        <AppointmentsSection filters={filters} />
        <CareCoordinationSection filters={filters} />
        <EnrollmentStatusSection filters={filters} />
        <MemberStatusSection filters={filters} />
        <MemberAttributesSection filters={filters} />
      </VStack>
    </Box>

    {/* Non-scrolling footer: stays put at the bottom of the panel while the
        sections above scroll underneath it. Filters don't reach the table
        until this is clicked — batches what would otherwise be one request
        per change into one per Apply. Disabled until draft state actually
        differs from what's applied, so there's nothing to confirm when
        nothing has changed. */}
    <Box
      flexShrink={0}
      px={4}
      py={3}
      borderTopWidth="1px"
      borderColor="cloud"
      bg="feather"
    >
      <Button
        w="100%"
        onClick={filters.applyFilters}
        disabled={!filters.isDirty}
      >
        Apply Filters
      </Button>
    </Box>
  </Box>
);
